import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Monitor, Shield, Download, ExternalLink, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewDesktopTokenWizard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showProvisionDialog, setShowProvisionDialog] = useState(false);
  const [tokenData, setTokenData] = useState(null);

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  const confirmCancel = () => {
    setShowCancelDialog(false);
    navigate('/dashboard');
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Validate PIN
      if (pin.length < 4 || pin.length > 8 || !/^\d+$/.test(pin)) {
        toast.error('PIN must be 4-8 digits');
        return;
      }
      
      setLoading(true);
      try {
        // Call backend to create desktop token (same API as soft token)
        const response = await axios.post(`${API}/softtoken/create?username=${user.username}`, {
          pin: pin
        });

        if (response.data.success) {
          setTokenData(response.data);
          setCurrentStep(3);
        }
      } catch (error) {
        console.error('Error creating desktop token:', error);
        const errorMessage = error.response?.data?.detail || 'Failed to create desktop token';
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleOpenProvisionLink = () => {
    setShowProvisionDialog(true);
  };

  const confirmOpenProvisionLink = () => {
    setShowProvisionDialog(false);
    window.open(tokenData.token_link, '_blank');
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center">
          <Monitor className="h-8 w-8 text-purple-600" />
        </div>
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-foreground">Desktop Token</h2>
          <p className="text-sm text-muted-foreground font-ibm">Step 1 of 3: Introduction</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-ibm font-semibold text-lg text-blue-900 mb-3">What is a Desktop Token?</h3>
          <p className="text-blue-800 font-ibm text-sm leading-relaxed">
            A desktop token is a software token that runs in Windows desktop or laptop computers, suitable for environments where mobile devices are not allowed.
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
          <h3 className="font-ibm font-semibold text-base text-slate-900 mb-4">
            Please download and install our desktop app:
          </h3>
          <a
            href="https://downloads.mi-token.com/v4394/WindowsSoftTokenInstaller.exe"
            download
            className="inline-flex items-center gap-2 bg-emerald hover:bg-emerald/90 text-white px-6 py-3 rounded-lg font-ibm font-medium transition-colors"
          >
            <Download className="h-5 w-5" />
            Download Desktop App
          </a>
          <p className="text-xs text-slate-600 font-ibm mt-3">
            Compatible with Windows 7, 8, 10, and 11
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald font-semibold text-sm">1</span>
            </div>
            <div>
              <p className="font-ibm text-sm text-slate-700 font-medium">Download and install the desktop app</p>
              <p className="font-ibm text-xs text-slate-600 mt-1">Run the installer and follow the installation wizard</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald font-semibold text-sm">2</span>
            </div>
            <div>
              <p className="font-ibm text-sm text-slate-700 font-medium">Set up your token PIN</p>
              <p className="font-ibm text-xs text-slate-600 mt-1">Choose a secure 4-8 digit PIN in the next step</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald font-semibold text-sm">3</span>
            </div>
            <div>
              <p className="font-ibm text-sm text-slate-700 font-medium">Provision your token</p>
              <p className="font-ibm text-xs text-slate-600 mt-1">Follow the instructions to activate your desktop token</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-6">
        <Button
          type="button"
          onClick={handleNext}
          className="flex-1 h-12 font-ibm font-medium bg-emerald hover:bg-emerald/90"
        >
          Next Step
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          className="h-12 font-ibm"
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center">
          <Shield className="h-8 w-8 text-purple-600" />
        </div>
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-foreground">Set Token PIN</h2>
          <p className="text-sm text-muted-foreground font-ibm">Step 2 of 3: Security Setup</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <h3 className="font-ibm font-semibold text-base text-amber-900 mb-2">Important</h3>
          <p className="text-amber-800 font-ibm text-sm leading-relaxed">
            Choose a secure PIN that you'll remember. You'll need this PIN every time you use your desktop token to authenticate.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pin" className="font-ibm font-medium">
            Token PIN <span className="text-red-500">*</span>
          </Label>
          <Input
            id="pin"
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Enter 4-8 digit PIN"
            maxLength={8}
            className="h-12 font-ibm text-lg tracking-widest"
            required
          />
          <p className="text-xs text-muted-foreground font-ibm">
            PIN must be 4-8 digits (numbers only)
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <h4 className="font-ibm font-medium text-sm text-slate-900 mb-2">PIN Requirements:</h4>
          <ul className="space-y-1 text-xs text-slate-600 font-ibm">
            <li className="flex items-center gap-2">
              <span className={pin.length >= 4 && pin.length <= 8 ? 'text-green-600' : 'text-slate-400'}>●</span>
              Between 4 and 8 digits
            </li>
            <li className="flex items-center gap-2">
              <span className={/^\d+$/.test(pin) && pin.length > 0 ? 'text-green-600' : 'text-slate-400'}>●</span>
              Numbers only (0-9)
            </li>
          </ul>
        </div>
      </div>

      <div className="flex gap-3 pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          className="h-12 font-ibm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          type="button"
          onClick={handleNext}
          disabled={loading || !pin || pin.length < 4 || pin.length > 8 || !/^\d+$/.test(pin)}
          className="flex-1 h-12 font-ibm font-medium bg-emerald hover:bg-emerald/90"
        >
          {loading ? 'Creating Token...' : 'Create Token'}
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center">
          <Monitor className="h-8 w-8 text-green-600" />
        </div>
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-foreground">Token Created!</h2>
          <p className="text-sm text-muted-foreground font-ibm">Step 3 of 3: Provision Token</p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <p className="text-green-800 font-ibm text-sm leading-relaxed">
            ✓ Your desktop token has been created successfully! Token name: <strong>{tokenData?.token_name}</strong>
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="font-ibm font-semibold text-base text-slate-900 mb-4">
              Open your desktop app and create a new token
            </h3>
            <img 
              src="https://customer-assets.emergentagent.com/job_idtrust-portal/artifacts/080r2fak_image.png" 
              alt="Desktop app menu showing New Token option"
              className="w-full max-w-md mx-auto rounded-lg border border-slate-300 shadow-sm"
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h3 className="font-ibm font-semibold text-base text-slate-900 mb-4">
              Copy the 8-digit code shown:
            </h3>
            <img 
              src="https://customer-assets.emergentagent.com/job_idtrust-portal/artifacts/c2lmb4mp_image.png" 
              alt="Token initialization screen showing 8-digit loading code"
              className="w-full max-w-md mx-auto rounded-lg border border-slate-300 shadow-sm"
            />
            <p className="text-xs text-slate-600 font-ibm mt-3 text-center">
              The 8-digit code is highlighted in yellow
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-ibm font-semibold text-base text-blue-900 mb-4">
              And click the following link:
            </h3>
            <Button
              onClick={handleOpenProvisionLink}
              className="w-full h-12 bg-emerald hover:bg-emerald/90 text-white font-ibm font-medium"
            >
              <ExternalLink className="mr-2 h-5 w-5" />
              Provision Desktop Token
            </Button>
            <p className="text-xs text-blue-700 font-ibm mt-3 text-center">
              This will open Mi-Token's mobile site in a new tab
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-6">
        <Button
          type="button"
          onClick={() => navigate('/dashboard/manage-tokens')}
          className="flex-1 h-12 font-ibm font-medium bg-emerald hover:bg-emerald/90"
        >
          Go to My Tokens
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="h-12 font-ibm"
        >
          Back to Dashboard
        </Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Button
        variant="ghost"
        onClick={handleCancel}
        className="gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      <Card className="p-8 border border-border">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </Card>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">Cancel Token Creation?</AlertDialogTitle>
            <AlertDialogDescription className="font-ibm">
              Are you sure you want to cancel? Your progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-ibm">No, Continue</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} className="font-ibm bg-red-600 hover:bg-red-700">
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Provision Link Warning Dialog */}
      <AlertDialog open={showProvisionDialog} onOpenChange={setShowProvisionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">Open Provisioning Link?</AlertDialogTitle>
            <AlertDialogDescription className="font-ibm">
              You will be redirected to Mi-Token's mobile site to provision the new token. 
              Make sure you have copied the 8-digit code from your desktop app before proceeding.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-ibm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOpenProvisionLink} className="font-ibm bg-emerald hover:bg-emerald/90">
              Yes, Open Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
